import { ComponentType, FC, ReactNode } from 'react';
import { BsQuestionCircle } from 'react-icons/bs';
import { match, Pattern } from 'ts-pattern';
import {
  Box,
  BoxProps,
  Heading,
  Icon,
  Image,
  ImageProps,
  Separator,
  Stack,
  StackProps,
  Text,
  useSlotRecipe,
} from '@chakra-ui/react';

import {
  ContractDark,
  PhoneCallDark,
  ThumbsUpDark,
} from '~/core/shared/assets';
import { CircleIconPresenter } from '~/core/shared/components';
import { useRootDocument } from '~/core/shared/hooks';
import { Tooltip } from '~/core/shared/lib/chakraUi/components';
import {
  AnyDocument,
  WorkflowDefaultStepContentBlockHeadingObject,
  WorkflowDefaultStepContentBlockIcon,
} from '~/core/shared/types';

// @IMPROVEMENT Move this to use useMultiStyleConfig()?

export type ContentBlockProps = {
  description?: ReactNode;
  divider?: boolean;
  marginBottom?: number;
  tooltip?: boolean;
  tooltipLabel?: string;
  heading?: string | WorkflowDefaultStepContentBlockHeadingObject;
  icon?: ComponentType | WorkflowDefaultStepContentBlockIcon;
  image?: {
    props?: ImageProps;
    boxProps?: BoxProps;
    srcResolver?: (rootDocument: AnyDocument) => string | undefined;
  };
  stackProps?: StackProps;
  htmlContent?: ReactNode;
};

// !@TODO This needs to be passed through config
const getIconComponent = (icon: WorkflowDefaultStepContentBlockIcon): FC => {
  switch (icon) {
    case 'call':
      return PhoneCallDark;

    case 'contract':
      return ContractDark;

    case 'thumbs-up':
      return ThumbsUpDark;
    default:
      throw new Error(`Unknown icon type: ${icon}`);
  }
};

export const ContentBlock: FC<ContentBlockProps> = ({
  tooltip,
  tooltipLabel,
  description,
  divider,
  heading,
  icon,
  image,
  stackProps,
  htmlContent,
  marginBottom,
}) => {
  const recipe = useSlotRecipe({ key: 'field' });
  const { tooltipIcon } = recipe();

  const renderHeadingContent = (
    headingContent: string | WorkflowDefaultStepContentBlockHeadingObject,
  ): ReactNode => {
    if (typeof headingContent === 'string') {
      return headingContent;
    }

    const { showOptionalIndicator, showRequiredIndicator, text } =
      headingContent;
    return (
      <>
        {text}

        {(showOptionalIndicator || showRequiredIndicator) && (
          <Box as="span" color="primary.500">
            {showOptionalIndicator && ' (optional)'}
            {showRequiredIndicator && '*'}
          </Box>
        )}
      </>
    );
  };

  const rootDocument = useRootDocument();

  return (
    <Stack gap={4} {...stackProps} mb={marginBottom}>
      {icon && (
        <CircleIconPresenter
          icon={typeof icon === 'string' ? getIconComponent(icon) : icon}
          {...((heading || description) && { mb: 2 })}
        />
      )}

      {heading && (
        <Heading
          size="lg"
          {...(typeof heading !== 'string' ? heading.props : {})}
        >
          {renderHeadingContent(heading)}
          {tooltip && (
            <Tooltip content={tooltipLabel}>
              <Box as="span" css={tooltipIcon} ml={2}>
                <Icon as={BsQuestionCircle} boxSize={4} />
              </Box>
            </Tooltip>
          )}
        </Heading>
      )}

      {image && (
        <Box {...(image?.boxProps || {})}>
          <Image
            src={
              image.srcResolver && rootDocument
                ? image.srcResolver(rootDocument)
                : undefined
            }
            {...(image?.props || {})}
          />
        </Box>
      )}

      {description &&
        match(description)
          .with(Pattern.string, () => <Text>{description}</Text>)
          .otherwise(() => description)}

      {htmlContent}

      {divider && (
        <Separator
          variant="heading" /* @IMPROVEMENT Don't want to default this variant here */
        />
      )}
    </Stack>
  );
};
